import type { NavigateFunction } from 'react-router';
import logger from '@ayocore/platform/common/helpers/logger';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import type {
  DealRepository,
  DealFinancialSummary,
} from '@ayocore/erp/crm/server/repositories/DealRepository';
import type { DealStatusRepository } from '@ayocore/erp/crm/server/repositories/DealStatusRepository';
import type { Deal } from '@ayocore/erp/crm/schemas/deal.schema';
import type { DealStatus } from '@ayocore/erp/crm/schemas/deal-status.schema';
import { CRM_DEAL_STATUSES } from '@ayocore/erp/crm/constants';

import { extractErrorMessage } from '@ayocore/platform/client/utils';
import type { DealEvent } from '../../../../schemas/deal-event.schema';
import type { DealEventRepository } from '../../../../server/repositories/DealEventRepository';

/**
 * Расширенный тип сделки для безопасного рендеринга relation-объектов.
 */
export type DealExtended = Deal & {
  partner?: { id?: number; name?: string; full_name?: string } | null;
  deal_status?: { id?: number; slug?: string; name?: string; color?: string | null } | null;
  deal_type?: { id?: number; slug?: string; name?: string } | null;
  deal_owner?: { id?: number; name?: string; email?: string } | null;
  currency?: { id?: number; name?: string; code?: string; symbol?: string } | null;
  created_by?: { id?: number; name?: string } | null;
  updated_by?: { id?: number; name?: string } | null;
  /** Сумма позиций (вычисляемая колонка из withRowsTotal). */
  rows_total?: number | null;
};

/**
 * Состояние контроллера DealView.
 */
export interface DealViewState {
  deal: DealExtended | null;
  statusHistory: DealEvent[];
  financialSummary: DealFinancialSummary | null;
  loading: boolean;
  error: string | null;
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    /** Право редактировать `close_date_fact` (только CRM_OWNER в grasias). */
    canEditCloseDate: boolean;
    loading: boolean;
  };
  modals: {
    edit: boolean;
    delete: boolean;
    statusChange: boolean;
  };
  statusChangeTarget: DealStatus | null;
  statusChangeError: string | null;
  statusChanging: boolean;
}

/**
 * DealViewController — контроллер для управления состоянием DealView.
 */
export class DealViewController {
  private state: DealViewState = {
    deal: null,
    statusHistory: [],
    financialSummary: null,
    loading: true,
    error: null,
    permissions: {
      canEdit: false,
      canDelete: false,
      canEditCloseDate: false,
      loading: true,
    },
    modals: {
      edit: false,
      delete: false,
      statusChange: false,
    },
    statusChangeTarget: null,
    statusChangeError: null,
    statusChanging: false,
  };
  private listeners: Set<() => void> = new Set();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): DealViewState => this.state;

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        logger.error('Error in DealViewController listener:', error);
      }
    });
  }

  private replaceState(patch: Partial<DealViewState>): void {
    this.state = { ...this.state, ...patch };
  }

  /**
   * Загрузить сделку и базовые связи.
   */
  async load(
    dealId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    dealRepository: AwaitableRpcClient<DealRepository>,
    dealEventRepository: AwaitableRpcClient<DealEventRepository>
  ): Promise<void> {
    if (!dealId || Number.isNaN(dealId)) {
      this.replaceState({ error: 'Неверный ID сделки', loading: false });
      this.notifyListeners();
      return;
    }

    try {
      this.replaceState({ loading: true, error: null });
      this.notifyListeners();

      const [dealResult, statusHistoryResult, financialSummaryResult] = await Promise.all([
        dealRepository.withRelations().withRowsTotal().findById(dealId),
        dealEventRepository.getStatusHistoryForDeal(dealId),
        dealRepository.getDealFinancialSummary(dealId),
      ]);

      if (!dealResult) {
        this.replaceState({ error: `Сделка с ID ${dealId} не найдена`, loading: false });
        if (!isModal) {
          setTabTitle(`Сделка #${dealId} - Не найдена`);
        }
        this.notifyListeners();
        return;
      }

      const dealExtended = dealResult as DealExtended;
      this.replaceState({
        deal: dealExtended,
        statusHistory: statusHistoryResult || [],
        financialSummary: financialSummaryResult ?? null,
        loading: false,
      });

      if (!isModal) {
        setTabTitle(`Сделка ${dealExtended.number || `#${dealExtended.id ?? dealId}`}`);
      }

      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to fetch deal data:', error);
      this.replaceState({ error: 'Не удалось загрузить данные сделки', loading: false });
      if (!isModal) {
        setTabTitle(`Сделка #${dealId} - Ошибка`);
      }
      this.notifyListeners();
    }
  }

  /**
   * Проверить права доступа для сделки.
   */
  async checkPermissions(
    dealId: number,
    dealRepository: AwaitableRpcClient<DealRepository>
  ): Promise<void> {
    if (!dealId || Number.isNaN(dealId)) {
      this.replaceState({
        permissions: { canEdit: false, canDelete: false, canEditCloseDate: false, loading: false },
      });
      this.notifyListeners();
      return;
    }

    try {
      this.replaceState({
        permissions: { ...this.state.permissions, loading: true },
      });
      this.notifyListeners();

      const [canUpdateResult, canDeleteResult, canEditCloseDateResult] = await Promise.all([
        dealRepository.canUpdate(dealId),
        dealRepository.canDelete(dealId),
        dealRepository.canUpdate(dealId, 'close_date_fact'),
      ]);

      this.replaceState({
        permissions: {
          canEdit: canUpdateResult.allowed,
          canDelete: canDeleteResult.allowed,
          canEditCloseDate: canEditCloseDateResult.allowed,
          loading: false,
        },
      });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to check deal permissions:', error);
      this.replaceState({
        permissions: { canEdit: false, canDelete: false, canEditCloseDate: false, loading: false },
      });
      this.notifyListeners();
    }
  }

  /**
   * Открыть модалку редактирования.
   */
  openEdit(): void {
    this.replaceState({
      modals: { ...this.state.modals, edit: true },
    });
    this.notifyListeners();
  }

  /**
   * Закрыть модалку редактирования.
   */
  closeEdit(): void {
    this.replaceState({
      modals: { ...this.state.modals, edit: false },
    });
    this.notifyListeners();
  }

  /**
   * Открыть модалку удаления.
   */
  openDelete(): void {
    this.replaceState({
      modals: { ...this.state.modals, delete: true },
    });
    this.notifyListeners();
  }

  /**
   * Закрыть модалку удаления.
   */
  closeDelete(): void {
    this.replaceState({
      modals: { ...this.state.modals, delete: false },
    });
    this.notifyListeners();
  }

  /**
   * Удалить сделку и перенаправить на список сделок.
   *
   * Возвращает сообщение об ошибке (или null при успехе) — вызывающий показывает
   * его уведомлением. Сообщение приходит с сервера («Нельзя удалить сделку: по
   * ней есть расходы» и т.п.), поэтому его нельзя заменять общим текстом: именно
   * оно объясняет пользователю, почему удаление не прошло. Карточку сделки при
   * отказе не ломаем — модалку удаления просто закрываем.
   */
  async deleteDeal(
    dealId: number,
    navigate: NavigateFunction,
    dealRepository: AwaitableRpcClient<DealRepository>
  ): Promise<string | null> {
    if (!dealId) return null;

    try {
      await dealRepository.deleteById(dealId);
      this.closeDelete();
      navigate('/crm/deals');
      return null;
    } catch (error) {
      logger.error('Failed to delete deal:', error);
      this.closeDelete();
      return extractErrorMessage(error, 'Не удалось удалить сделку');
    }
  }

  /**
   * Обновить поле сделки и обновить deal в состоянии (без loading, без полной перезагрузки).
   * Как refreshPartner в PartnerViewController.
   */
  async updateDealField<K extends keyof Deal>(
    dealId: number,
    field: K,
    value: Deal[K],
    dealRepository: AwaitableRpcClient<DealRepository>
  ): Promise<void> {
    if (!dealId || Number.isNaN(dealId)) return;

    try {
      await dealRepository.update(dealId, { [field]: value } as Partial<Deal>);
      const dealResult = await dealRepository.withRelations().withRowsTotal().findById(dealId);
      if (dealResult && this.state.deal) {
        this.replaceState({ deal: dealResult as DealExtended });
        this.notifyListeners();
      }
    } catch (error) {
      logger.error('Failed to update deal field:', error);
      this.replaceState({ error: 'Не удалось обновить сделку' });
      this.notifyListeners();
    }
  }

  /**
   * Открыть модалку смены статуса с выбранным целевым статусом.
   */
  openStatusChange(status: DealStatus): void {
    this.replaceState({
      modals: { ...this.state.modals, statusChange: true },
      statusChangeTarget: status,
      statusChangeError: null,
    });
    this.notifyListeners();
  }

  /**
   * Закрыть модалку смены статуса и сбросить ошибку.
   */
  closeStatusChange(): void {
    this.replaceState({
      modals: { ...this.state.modals, statusChange: false },
      statusChangeTarget: null,
      statusChangeError: null,
    });
    this.notifyListeners();
  }

  /**
   * Изменить статус сделки. При ошибке — показывает её внутри модалки (не закрывает).
   * При успехе — перезагружает deal и statusHistory, закрывает модалку.
   *
   * @param cancelComment причина перехода в негативный статус («Отменён») —
   *   уходит в `deal.cancel_comment` тем же патчем, чтобы серверные правила
   *   видели её сразу при проверке статуса.
   */
  async changeStatus(
    dealId: number,
    dealRepository: AwaitableRpcClient<DealRepository>,
    dealEventRepository: AwaitableRpcClient<DealEventRepository>,
    cancelComment?: string
  ): Promise<void> {
    const targetStatusId = this.state.statusChangeTarget?.id;
    if (!targetStatusId) return;

    this.replaceState({ statusChanging: true, statusChangeError: null });
    this.notifyListeners();

    try {
      await dealRepository.update(dealId, {
        deal_status_id: targetStatusId,
        ...(cancelComment !== undefined ? { cancel_comment: cancelComment } : {}),
      });

      const [dealResult, statusHistoryResult] = await Promise.all([
        dealRepository.withRelations().withRowsTotal().findById(dealId),
        dealEventRepository.getStatusHistoryForDeal(dealId),
      ]);

      if (dealResult) {
        this.replaceState({
          deal: dealResult as DealExtended,
          statusHistory: statusHistoryResult || [],
          statusChanging: false,
          modals: { ...this.state.modals, statusChange: false },
          statusChangeTarget: null,
          statusChangeError: null,
        });
      } else {
        this.replaceState({
          statusChanging: false,
          statusChangeError: 'Не удалось загрузить обновлённую сделку',
        });
      }
      this.notifyListeners();
    } catch (error) {
      let message = 'Не удалось изменить статус';
      if (error instanceof Error) {
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        const axiosError = error as any;
        if (axiosError.response?.data?.error?.message) {
          message = axiosError.response.data.error.message;
        } else if (axiosError.response?.data?.message) {
          message = axiosError.response.data.message;
        } else {
          message = error.message;
        }
      }
      this.replaceState({ statusChanging: false, statusChangeError: message });
      this.notifyListeners();
    }
  }

  /**
   * Проверяет возможность архивирования сделки.
   * Возвращает { allowed, message } — message содержит предупреждение или причину отказа.
   */
  async checkCanArchive(
    dealId: number,
    dealRepository: AwaitableRpcClient<DealRepository>
  ): Promise<{ allowed: boolean; message?: string }> {
    try {
      return await dealRepository.canUpdate(dealId, 'archived');
    } catch (error) {
      logger.error('Failed to check archive permission:', error);
      return { allowed: false, message: extractErrorMessage(error, 'Не удалось проверить права') };
    }
  }

  /**
   * Архивировать сделку. Вызывать после checkCanArchive + подтверждения пользователя.
   * Возвращает null при успехе или сообщение об ошибке.
   */
  async archiveDeal(
    dealId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    dealRepository: AwaitableRpcClient<DealRepository>,
    dealEventRepository: AwaitableRpcClient<DealEventRepository>
  ): Promise<string | null> {
    try {
      await dealRepository.update(dealId, { archived: true } as Partial<Deal>);
      await this.reload(
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository
      );
      return null;
    } catch (error) {
      logger.error('Failed to archive deal:', error);
      return extractErrorMessage(error, 'Не удалось архивировать сделку');
    }
  }

  /**
   * Убрать сделку из архива. Возвращает null при успехе или сообщение об ошибке.
   */
  async unarchiveDeal(
    dealId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    dealRepository: AwaitableRpcClient<DealRepository>,
    dealEventRepository: AwaitableRpcClient<DealEventRepository>
  ): Promise<string | null> {
    try {
      await dealRepository.update(dealId, { archived: false } as Partial<Deal>);
      await this.reload(
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository
      );
      return null;
    } catch (error) {
      logger.error('Failed to unarchive deal:', error);
      return extractErrorMessage(error, 'Не удалось убрать сделку из архива');
    }
  }

  /**
   * Находит статус «Отменена» для типа сделки — нужен, чтобы пункт меню
   * «Отменить» открывал ту же модалку смены статуса (с полем причины), а не
   * менял статус втихую: причина отмены обязательна в инстансах вроде grasias.
   *
   * @returns статус или null, если он не настроен для этого типа сделки.
   */
  async findCancelStatus(
    dealStatusRepository: AwaitableRpcClient<DealStatusRepository>
  ): Promise<DealStatus | null> {
    const deal = this.state.deal;
    if (!deal) return null;

    const statuses = await dealStatusRepository.filterByType(deal.deal_type_id).fetch();
    return statuses.find(s => s.slug === CRM_DEAL_STATUSES.SALE_CANCELED) ?? null;
  }

  /**
   * Отменить сделку (перевести в статус SALE_CANCELED). Возвращает null при успехе или сообщение об ошибке.
   *
   * @deprecated используйте `findCancelStatus` + `openStatusChange`: модалка
   *   смены статуса запрашивает причину отмены, без которой правила инстанса
   *   могут отклонить переход.
   */
  async cancelDeal(
    dealId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    dealRepository: AwaitableRpcClient<DealRepository>,
    dealEventRepository: AwaitableRpcClient<DealEventRepository>,
    dealStatusRepository: AwaitableRpcClient<DealStatusRepository>
  ): Promise<string | null> {
    const deal = this.state.deal;
    if (!deal) return 'Сделка не загружена';

    try {
      const statuses = await dealStatusRepository.filterByType(deal.deal_type_id).fetch();
      const cancelStatus = statuses.find(s => s.slug === CRM_DEAL_STATUSES.SALE_CANCELED);
      if (!cancelStatus) return 'Статус «Отменена» не найден';

      await dealRepository.update(dealId, { deal_status_id: cancelStatus.id } as Partial<Deal>);
      await this.reload(
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository
      );
      return null;
    } catch (error) {
      logger.error('Failed to cancel deal:', error);
      return extractErrorMessage(error, 'Не удалось отменить сделку');
    }
  }

  /**
   * Перезагрузить сделку.
   */
  async reload(
    dealId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    dealRepository: AwaitableRpcClient<DealRepository>,
    dealEventRepository: AwaitableRpcClient<DealEventRepository>
  ): Promise<void> {
    await this.load(dealId, isModal, setTabTitle, navigate, dealRepository, dealEventRepository);
  }
}
